import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { 
  WebSocketMessage, 
  TelemetryUpdate, 
  AlertNotification, 
  SimulationStateUpdate,
  ConnectionStatusUpdate 
} from '@cbm/shared';

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WebSocketContextType {
  connectionState: ConnectionState;
  isConnected: boolean;
  subscribeToMachine: (machineId: string) => void;
  unsubscribeFromMachine: (machineId: string) => void;
  sendMessage: (message: any) => void;
  lastMessage: WebSocketMessage | null;
  connectionCount: number;
}

export interface WebSocketEventHandlers {
  onTelemetryUpdate?: (data: TelemetryUpdate) => void;
  onAlertNotification?: (data: AlertNotification) => void;
  onSimulationStateUpdate?: (data: SimulationStateUpdate) => void;
  onConnectionStatusUpdate?: (data: ConnectionStatusUpdate) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
  eventHandlers?: WebSocketEventHandlers;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { isAuthenticated, token } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [connectionCount, setConnectionCount] = useState(0);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const subscribedMachinesRef = useRef<Set<string>>(new Set());
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;

  const resolveTenantId = useCallback((): string => {
    const envTenant = (import.meta as any).env?.VITE_TENANT_ID as string | undefined;
    if (envTenant && envTenant.trim()) return envTenant.trim();
    return 'demo-tenant';
  }, []);

  const getReconnectDelay = () => {
    return Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts.current), 30000);
  };

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        ...message,
        timestamp: new Date().toISOString(),
        correlationId: crypto.randomUUID()
      }));
    }
  }, []);

  const subscribeToMachine = useCallback((machineId: string) => {
    console.log('[WS] 📡 Subscribing to machine:', machineId);
    subscribedMachinesRef.current.add(machineId);
    sendMessage({ action: 'subscribe_machine', machineId });
  }, [sendMessage]);

  const unsubscribeFromMachine = useCallback((machineId: string) => {
    console.log('[WS] 📡 Unsubscribing from machine:', machineId);
    subscribedMachinesRef.current.delete(machineId);
    sendMessage({ action: 'unsubscribe_machine', machineId });
  }, [sendMessage]);

  const connect = useCallback(() => {
    console.log('[WS] Connect attempt:', { isAuthenticated, hasToken: !!token, currentState: wsRef.current?.readyState });
    
    if (!isAuthenticated || !token || wsRef.current?.readyState === WebSocket.CONNECTING || wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[WS] Connect blocked:', { isAuthenticated, hasToken: !!token, wsState: wsRef.current?.readyState });
      return;
    }

    setConnectionState('connecting');

    try {
      const tenantId = resolveTenantId();
      const wsUrl = `${import.meta.env.VITE_WEBSOCKET_URL}?token=${encodeURIComponent(token)}&tenantId=${encodeURIComponent(tenantId)}`;
      console.log('[WS] Connecting to:', wsUrl.substring(0, 50) + '...');
      console.log('[WS] TenantId:', tenantId);
      console.log('[WS] Env VITE_WEBSOCKET_URL:', import.meta.env.VITE_WEBSOCKET_URL);
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setConnectionState('connected');
        reconnectAttempts.current = 0;
        console.log('[WS] ✅ Connected successfully');

        // Re-subscribe to previously subscribed machines after reconnect
        const machines = Array.from(subscribedMachinesRef.current);
        console.log('[WS] Re-subscribing to machines:', machines);
        if (machines.length > 0) {
          machines.forEach((m) => sendMessage({ action: 'subscribe_machine', machineId: m }));
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          if (!event.data?.trim()) {
            console.log('[WS] Empty message received');
            return;
          }
          const message: WebSocketMessage = JSON.parse(event.data);
          if (!message?.type) {
            console.log('[WS] Message without type:', message);
            return;
          }
          
          console.log('[WS] 📨 Message received:', message.type);
          
          // DIAGNOSTIC: Log every telemetry machineId
          if (message.type === 'telemetry_update') {
            const telemetry = message as TelemetryUpdate;
            console.log('[WS] 📊 Telemetry:', {
              machineId: telemetry.payload?.machineId,
              timestamp: telemetry.timestamp,
              sample: telemetry.payload?.sample
            });
          }
          
          setLastMessage(message);
          
          if (message.type === 'connection_status') {
            const count = (message as ConnectionStatusUpdate).payload.userCount;
            console.log('[WS] 👥 Connection status:', count, 'users');
            setConnectionCount(count);
          }
        } catch (error) {
          console.error('[WS] ❌ Message parse error:', error, 'Data:', event.data);
        }
      };

      wsRef.current.onclose = (event) => {
        setConnectionState('disconnected');
        console.warn('[WS] ⚠️ Closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        
        if (event.code !== 1000 && event.code !== 1008 && event.code !== 1002 && 
            isAuthenticated && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = getReconnectDelay();
          console.log(`[WS] 🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          console.log('[WS] Not reconnecting:', { code: event.code, isAuthenticated, attempts: reconnectAttempts.current });
        }
      };

      wsRef.current.onerror = (error) => {
        setConnectionState('error');
        console.error('[WS] ❌ Error:', error);
      };

    } catch (error) {
      setConnectionState('error');
    }
  }, [isAuthenticated, token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setConnectionState('disconnected');
    subscribedMachinesRef.current.clear();
    reconnectAttempts.current = 0;
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    } else {
      disconnect();
    }
    return disconnect;
  }, [isAuthenticated, token, connect, disconnect, resolveTenantId, sendMessage]);

  const contextValue: WebSocketContextType = {
    connectionState,
    isConnected: connectionState === 'connected',
    subscribeToMachine,
    unsubscribeFromMachine,
    sendMessage,
    lastMessage,
    connectionCount,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

export function useWebSocketSubscription(eventHandlers: WebSocketEventHandlers) {
  const { lastMessage } = useWebSocket();
  const handlersRef = useRef(eventHandlers);
  handlersRef.current = eventHandlers;

  useEffect(() => {
    if (!lastMessage) return;

    const handlers = handlersRef.current;
    switch (lastMessage.type) {
      case 'telemetry_update':
        handlers.onTelemetryUpdate?.(lastMessage as TelemetryUpdate);
        break;
      case 'alert_created':
      case 'alert_updated':
        handlers.onAlertNotification?.(lastMessage as AlertNotification);
        break;
      case 'simulation_state':
        handlers.onSimulationStateUpdate?.(lastMessage as SimulationStateUpdate);
        break;
      case 'connection_status':
        handlers.onConnectionStatusUpdate?.(lastMessage as ConnectionStatusUpdate);
        break;
    }
  }, [lastMessage]);
}